/** The "Views" menu of the accounts list: ready-made views, saved views, and copy-a-link. */
import { Bookmark, Link2, Trash2 } from "lucide-react";
import { useGetIdentity, useListContext, useNotify } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

import {
  deleteAccountView,
  presetAccountViews,
  saveAccountView,
  savedAccountViews,
  type AccountView,
} from "./accountViews";

export const AccountViewsMenu = () => {
  const { identity } = useGetIdentity();
  const { filterValues, setFilters } = useListContext();
  const notify = useNotify();
  const [saved, setSaved] = useState<AccountView[]>(savedAccountViews);
  const [name, setName] = useState("");

  const open = (view: AccountView) => setFilters(view.filter);

  const save = () => {
    const label = name.trim();
    if (!label) return;
    setSaved(saveAccountView(label, filterValues));
    setName("");
    notify(`View "${label}" saved in this browser`, { type: "success" });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notify("Link to this view copied", { type: "success" });
    } catch {
      notify("Couldn't copy: copy the address bar instead", {
        type: "warning",
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <Bookmark /> Views
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Ready-made</DropdownMenuLabel>
        {presetAccountViews(identity?.id).map((view) => (
          <DropdownMenuItem key={view.id} onSelect={() => open(view)}>
            {view.label}
          </DropdownMenuItem>
        ))}
        {saved.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Saved</DropdownMenuLabel>
            {saved.map((view) => (
              <DropdownMenuItem
                key={view.id}
                onSelect={() => open(view)}
                className="justify-between"
              >
                {view.label}
                <button
                  type="button"
                  aria-label={`Delete view ${view.label}`}
                  className="text-muted-foreground hover:text-destructive"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSaved(deleteAccountView(view.id));
                  }}
                >
                  <Trash2 className="size-3.5" />
                </button>
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
        <DropdownMenuSeparator />
        <form
          className="flex gap-2 p-1"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Input
            aria-label="Name for this view"
            placeholder="Save this view as…"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="h-8"
          />
          <Button type="submit" size="sm" disabled={!name.trim()}>
            Save
          </Button>
        </form>
        <DropdownMenuItem onSelect={copyLink}>
          <Link2 /> Copy a link to this view
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
